import clsx from 'clsx'
import FormField from 'components/FormField'
import { useStore } from 'context/store'
import { WorkspaceContext } from 'context/workspace.context'
import { observer } from 'mobx-react'
import { Dropdown } from 'primereact/dropdown'
import { Tag } from 'primereact/tag'
import { useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ScopeEnewLetter } from 'utils/constants/enewletter'
import { UserRoleEnum } from 'utils/constants/user'
import ScopeSelectbox from './ScopeSelectbox'
import TagMultipleSelect from './TagMultipleSelect'

const ScopeSending = () => {
  const { t } = useTranslation()

  const { watch, control, getValues, reset } = useFormContext()

  const scope = watch('sendTo')
  const tagIds = watch('tagIds')
  const { user } = useContext(WorkspaceContext)
  const { enewLetterStore } = useStore()

  const [previewData, setPreviewData] = useState<{
    matchedBranchCount: number
    recipientCount: number
  } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    if (
      scope === ScopeEnewLetter.Tag &&
      Array.isArray(tagIds) &&
      tagIds.length > 0
    ) {
      const ids = tagIds
        .map((t: any) => (typeof t === 'object' && t !== null ? t._id : t))
        .filter((id: any) => id && id !== ScopeEnewLetter.All)

      if (ids.length === 0) {
        setPreviewData(null)
        return
      }

      let isMounted = true
      setPreviewLoading(true)

      enewLetterStore
        .fetchAudiencePreview(ids)
        .then((res) => {
          if (isMounted) {
            setPreviewData(res)
          }
        })
        .catch(() => {
          if (isMounted) {
            setPreviewData(null)
          }
        })
        .finally(() => {
          if (isMounted) setPreviewLoading(false)
        })

      return () => {
        isMounted = false
      }
    } else {
      setPreviewData(null)
    }
  }, [tagIds, scope, enewLetterStore])

  return (
    <div className="flex flex-column gap-3">
      <div
        className="gap-4"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
        }}
      >
        <FormField
          name="sendTo"
          className=""
          control={control}
          required
          rules={{
            required: t('required', {
              field: t('To'),
            }),
          }}
          render={({ field, fieldState }) => {
            return (
              <Dropdown
                {...field}
                placeholder={t('Select Branch Customer or Event’s Customer')}
                options={[
                  {
                    label: t('Event’s Customer'),
                    value: ScopeEnewLetter.Event,
                  },
                  user.role === UserRoleEnum.Headquarter
                    ? {
                        label: t('Branch Customer'),
                        value: ScopeEnewLetter.Branch,
                      }
                    : {
                        label: t('All customer'),
                        value: ScopeEnewLetter.Branch,
                      },
                  ...(user.role === UserRoleEnum.Headquarter
                    ? [
                        {
                          label: t('By tag'),
                          value: ScopeEnewLetter.Tag,
                        },
                      ]
                    : []),
                ]}
                onChange={(e) => {
                  field.onChange(e)

                  reset({
                    ...getValues(),
                    sendingBranchIds: [],
                    sendingEventIds: [],
                    tagIds: [],
                  })
                }}
                className={clsx({
                  'p-invalid': fieldState.invalid,
                })}
              />
            )
          }}
          label={t('To')}
        />
        {scope === ScopeEnewLetter.Branch ? (
          user.role === UserRoleEnum.BranchAdmin ? null : (
            <FormField
              name="sendingBranchIds"
              className=""
              control={control}
              required
              rules={{
                validate: (value) => {
                  if (!value?.length)
                    return t('required', {
                      field: t('Branch'),
                    })
                  return null
                },
              }}
              render={({ field, fieldState }) => {
                return (
                  <ScopeSelectbox
                    {...field}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected)}
                    placeholder={t('Select branch')}
                    url={`api/v1/branches`}
                    chipVariant="neutral"
                    className={clsx({
                      'p-invalid': fieldState.invalid,
                    })}
                  />
                )
              }}
              label={t('Branch')}
            />
          )
        ) : scope === ScopeEnewLetter.Event ? (
          <FormField
            name="sendingEventIds"
            className=""
            control={control}
            required
            rules={{
              validate: (value) => {
                if (!value?.length)
                  return t('required', {
                    field: t('Event'),
                  })
                return null
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <ScopeSelectbox
                  {...field}
                  value={field.value}
                  onChange={(selected) => field.onChange(selected)}
                  optionLabel="title"
                  placeholder={t('Select event')}
                  url={`api/v1/event`}
                  chipVariant="neutral"
                  className={clsx({
                    'p-invalid': fieldState.invalid,
                  })}
                />
              )
            }}
            label={t('Event')}
          />
        ) : scope === ScopeEnewLetter.Tag ? (
          <FormField
            name="tagIds"
            className=""
            control={control}
            required
            rules={{
              validate: (value) => {
                if (!value?.length)
                  return t('required', {
                    field: t('Tag'),
                  })
                return null
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <TagMultipleSelect
                  {...field}
                  value={field.value}
                  onChange={(selected) => field.onChange(selected)}
                  placeholder={t('Select tag')}
                  className={clsx({
                    'p-invalid': fieldState.invalid,
                  })}
                />
              )
            }}
            label={t('Tag')}
          />
        ) : null}
      </div>

      {scope === ScopeEnewLetter.Tag &&
        (previewLoading || previewData) && (
          <div className="p-3 surface-100 mb-4 border-round flex align-items-center justify-content-between">
            {previewLoading ? (
              <div className="flex align-items-center gap-2 text-gray-600">
                <i className="isax isax-refresh text-primary"></i>
                <span>{t('Calculating audience preview...')}</span>
              </div>
            ) : previewData ? (
              <div className="flex gap-4 align-items-center">
                <div>
                  <span className="text-gray-600">
                    {t('Matched Branches')}:{' '}
                  </span>
                  <span className="font-bold text-gray-900">
                    {previewData.matchedBranchCount}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">
                    {t('Total Recipients')}:{' '}
                  </span>
                  <span className="font-bold text-primary">
                    {previewData.recipientCount}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        )}
    </div>
  )
}
export default observer(ScopeSending)
